import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TeamPermissionsProvider } from './contexts/TeamPermissionsContext';
import ProtectedRoute from './components/ProtectedRoute';
import MasterRoute from './components/MasterRoute';
import PublicRoute from './components/PublicRoute';
import AppShell from './components/AppShell';
import Auth from './components/Auth';
import LandingPage from './pages/LandingPage';
import TermosPage from './pages/TermosPage';
import PrivacidadePage from './pages/PrivacidadePage';
import ErpPlaygroundPage from './modules/erp/pages/ErpPlaygroundPage';
import PublicDirectBookingPage from './pages/PublicDirectBookingPage';
import PublicStaffPortalPage from './pages/PublicStaffPortalPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import PublicCalculatorPage from './pages/PublicCalculatorPage';
import BatchTaxSimulatorPage from './pages/BatchTaxSimulatorPage';
import { trackEvent } from './utils/telemetry';

const ServicesLandingPage = React.lazy(() => import('./pages/landing/ServicesLandingPage'));
const AssistenciaCelularLandingPage = React.lazy(() => import('./pages/landing/AssistenciaCelularLandingPage'));
const SpeedPosPage = React.lazy(() => import('./pages/SpeedPosPage'));
const IndustriaPage = React.lazy(() => import('./pages/IndustriaPage'));
const VetLandingPage = React.lazy(() => import('./pages/landing/VetLandingPage'));
const MedicalLandingPage = React.lazy(() => import('./pages/landing/MedicalLandingPage'));
const BeautyLandingPage = React.lazy(() => import('./pages/landing/BeautyLandingPage'));
const SorveteriaLandingPage = React.lazy(() => import('./pages/landing/SorveteriaLandingPage'));
const IndustriaLandingPage = React.lazy(() => import('./pages/landing/IndustriaLandingPage'));
const ProfileSelectionPage = React.lazy(() => import('./pages/ProfileSelectionPage'));
import { checkAndStorePlayStoreOrigin } from './utils/playstore';
import { safeStorage } from './utils/safeStorage';
import { FiscalService } from './services/fiscalService';

const ExternalRedirect = ({ url }: { url: string }) => {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-xs font-mono text-slate-500">Redirecionando...</div>;
};

export default function App() {
  useEffect(() => {
    // Aquecimento prévio e silencioso do backend de retaguarda (evita cold start no PDV)
    FiscalService.prewarmBackend();

    // Captura a origem da Google Play se presente na URL
    checkAndStorePlayStoreOrigin();

    // Captura o cupom de indicação da URL (ref) globalmente e salva no storage para persistência
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        safeStorage.setItem('rmax_referred_coupon', ref.trim().toUpperCase());
      }
    } catch (e) {
      console.warn('Erro ao processar cupom na URL:', e);
    }

    const handleOnline = () => {
      trackEvent('client_online', { timestamp: new Date().toISOString() });
    };

    const handleOffline = () => {
      trackEvent('client_offline', { timestamp: new Date().toISOString() });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  return (
    <AuthProvider>
      <TeamPermissionsProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/planos" element={<LandingPage scrollTo="precos" />} />
            <Route path="/precos" element={<LandingPage scrollTo="precos" />} />
            <Route path="/termos" element={<TermosPage />} />
            <Route path="/privacidade" element={<PrivacidadePage />} />
            <Route path="/influenciador" element={<ExternalRedirect url="https://resolvmax.com.br/influenciador" />} />
            <Route path="/lab-erp" element={<MasterRoute><ErpPlaygroundPage /></MasterRoute>} />
            <Route path="/speed-pos" element={<ProtectedRoute><React.Suspense fallback={<div className="h-screen bg-zinc-950 flex items-center justify-center text-white text-xs font-mono">CARREGANDO PDV...</div>}><SpeedPosPage defaultMode="buffet" /></React.Suspense></ProtectedRoute>} />
            <Route path="/pdv" element={<ProtectedRoute><React.Suspense fallback={<div className="h-screen bg-zinc-950 flex items-center justify-center text-white text-xs font-mono">CARREGANDO PDV...</div>}><SpeedPosPage defaultMode="buffet" /></React.Suspense></ProtectedRoute>} />
            <Route path="/granel" element={<ProtectedRoute><React.Suspense fallback={<div className="h-screen bg-zinc-950 flex items-center justify-center text-white text-xs font-mono">CARREGANDO PDV...</div>}><SpeedPosPage defaultMode="bulk_retail" /></React.Suspense></ProtectedRoute>} />
            <Route path="/emporio" element={<ProtectedRoute><React.Suspense fallback={<div className="h-screen bg-zinc-950 flex items-center justify-center text-white text-xs font-mono">CARREGANDO PDV...</div>}><SpeedPosPage defaultMode="bulk_retail" /></React.Suspense></ProtectedRoute>} />
            <Route path="/agro" element={<ProtectedRoute><React.Suspense fallback={<div className="h-screen bg-zinc-950 flex items-center justify-center text-white text-xs font-mono">CARREGANDO PDV...</div>}><SpeedPosPage defaultMode="bulk_retail" /></React.Suspense></ProtectedRoute>} />
            <Route path="/chao-de-fabrica" element={<ProtectedRoute><React.Suspense fallback={<div className="h-screen bg-zinc-950 flex items-center justify-center text-white text-xs font-mono">CARREGANDO INDÚSTRIA...</div>}><IndustriaPage /></React.Suspense></ProtectedRoute>} />
            <Route path="/pcp" element={<Navigate to="/chao-de-fabrica" replace />} />
            <Route path="/gestao-granel" element={<Navigate to="/app?view=home&vertical=retail_bulk" replace />} />
            <Route path="/empresa" element={<Navigate to="/app?view=home&vertical=retail_bulk" replace />} />
            <Route path="/agendar" element={<PublicDirectBookingPage />} />
            <Route path="/agendar/:slug" element={<PublicDirectBookingPage />} />
            <Route path="/equipe/:token" element={<PublicStaffPortalPage />} />
            <Route path="/profissional/:token" element={<PublicStaffPortalPage />} />
            <Route path="/convite/:token" element={<InviteAcceptPage />} />
            <Route path="/calculadora-preco-venda" element={<PublicCalculatorPage />} />
            <Route path="/calculadora-markup" element={<PublicCalculatorPage />} />
            <Route path="/calculadora-reforma-tributaria" element={<PublicCalculatorPage defaultTab="reforma" />} />
            <Route path="/simulador-reforma-lote" element={<BatchTaxSimulatorPage />} />
            <Route path="/auditoria-catalogo-lote" element={<Navigate to="/simulador-reforma-lote" replace />} />
            <Route path="/simulador-reforma-tributaria" element={<Navigate to="/calculadora-reforma-tributaria" replace />} />
            <Route path="/reforma-tributaria" element={<Navigate to="/calculadora-reforma-tributaria" replace />} />
            
            {/* Landing Pages Cirúrgicas por Nicho (Tema Claro) */}
            <Route path="/assistencia" element={<React.Suspense fallback={<div className="min-h-screen bg-white" />}><AssistenciaCelularLandingPage /></React.Suspense>} />
            <Route path="/celular" element={<Navigate to="/assistencia" replace />} />
            <Route path="/conserto-celular" element={<Navigate to="/assistencia" replace />} />

            <Route path="/servicos" element={<React.Suspense fallback={<div className="min-h-screen bg-white" />}><ServicesLandingPage /></React.Suspense>} />
            <Route path="/obras" element={<Navigate to="/servicos" replace />} />
            <Route path="/prestadores" element={<Navigate to="/servicos" replace />} />

            <Route path="/vet" element={<React.Suspense fallback={<div className="min-h-screen bg-white" />}><VetLandingPage /></React.Suspense>} />
            <Route path="/pet" element={<Navigate to="/vet" replace />} />
            <Route path="/veterinaria" element={<Navigate to="/vet" replace />} />

            <Route path="/med" element={<React.Suspense fallback={<div className="min-h-screen bg-white" />}><MedicalLandingPage /></React.Suspense>} />
            <Route path="/clinica" element={<Navigate to="/med" replace />} />
            <Route path="/medicina" element={<Navigate to="/med" replace />} />
            <Route path="/consultorio" element={<Navigate to="/med" replace />} />

            <Route path="/beleza" element={<React.Suspense fallback={<div className="min-h-screen bg-white" />}><BeautyLandingPage /></React.Suspense>} />
            <Route path="/estetica" element={<Navigate to="/beleza" replace />} />
            <Route path="/barbearia" element={<Navigate to="/beleza" replace />} />
            <Route path="/massagem" element={<Navigate to="/beleza" replace />} />

            <Route path="/sorveteria" element={<React.Suspense fallback={<div className="min-h-screen bg-white" />}><SorveteriaLandingPage /></React.Suspense>} />
            <Route path="/acai" element={<Navigate to="/sorveteria" replace />} />

            <Route path="/industria" element={<React.Suspense fallback={<div className="min-h-screen bg-white" />}><IndustriaLandingPage /></React.Suspense>} />
            <Route path="/confeccao" element={<Navigate to="/industria" replace />} />

            <Route path="/harpy" element={<ExternalRedirect url="https://resolvmax.com.br/harpy" />} />
            <Route path="/escola-segura" element={<ExternalRedirect url="https://resolvmax.com.br/harpy" />} />
            <Route path="/escolas" element={<ExternalRedirect url="https://resolvmax.com.br/harpy" />} />
            
            {/* Auth Route (Guest only) */}
            <Route 
              path="/auth" 
              element={
                <PublicRoute>
                  <Auth />
                </PublicRoute>
              } 
            />

            {/* Onboarding Triaging Route (Seleção de Perfil por Nicho) */}
            <Route 
              path="/selecionar-perfil" 
              element={
                <React.Suspense fallback={<div className="min-h-screen bg-slate-50 flex items-center justify-center text-xs font-sans text-slate-500 font-medium">Carregando perfil...</div>}>
                  <ProfileSelectionPage />
                </React.Suspense>
              } 
            />
            <Route path="/onboarding/perfil" element={<Navigate to="/selecionar-perfil" replace />} />

            {/* Protected App Routes */}
            <Route 
              path="/app/*" 
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              } 
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </TeamPermissionsProvider>
    </AuthProvider>
  );
}
